import { useState } from "react";
import api from "@/lib/api";
import { useApi, useIncremental } from "@/lib/cache";
import { LoadState, ListSentinel } from "@/components/LoadState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronDown, Search, Play, Upload, Link as LinkIcon, Archive, Pencil, Unlink, Trash2 } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { EditItemDialog } from "@/components/EditItemDialog";

// Phase 6.2: default view is what you can actually work with — beats that
// aren't tied to a song yet. Order intentionally puts Available first.
const FILTERS = [
  { k: "available", label: "Available" },
  { k: "in_use", label: "In Use" },
  { k: "all", label: "All" },
];

const BEAT_LABELS = { idea: "Idea", estructura: "Structure", drums: "Drums", arreglo: "Arrangement", detalles: "Details", mezcla: "Mix", exportado: "Exported" };

export default function Beats() {
  const beatsQ = useApi("/beats");
  const songsQ = useApi("/songs");
  const beats = beatsQ.data || [];
  const songs = songsQ.data || [];
  const setBeats = (fn) => beatsQ.setData((cur) => fn(cur || []));
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("available");
  const [q, setQ] = useState("");
  const [linkOpen, setLinkOpen] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const { play } = usePlayer();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const load = () => { beatsQ.reload(); songsQ.reload(); };

  const filtered = beats.filter((b) => {
    if (q && !b.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (filter === "available") return b.status === "available";
    if (filter === "in_use") return b.status === "in_use";
    return true;
  });
  const { visible, sentinelRef, hasMore } = useIncremental(filtered);

  const uploadAudio = async (beat, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "audio");
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/beats/${beat.id}/audio`, { file_id: data.id });
      toast.success("Audio uploaded"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const linkToSong = async (beat, songId) => {
    await api.patch(`/beats/${beat.id}`, { linked_song_id: songId });
    toast.success("Beat linked to song");
    setLinkOpen(null); load();
  };
  const unlink = async (beat) => {
    await api.post(`/beats/${beat.id}/unlink`);
    toast.success("Beat unlinked");
    load();
  };

  const toggleTask = async (beat, key) => {
    const cur = beat.checklist?.[key] || "pending";
    const next = cur === "done" ? "pending" : "done";
    const { data } = await api.post(`/beats/${beat.id}/checklist`, { task_key: key, state: next });
    setBeats((bs) => bs.map((b) => b.id === beat.id ? { ...b, checklist: data.checklist, progress: data.progress } : b));
  };

  const permanentDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/beats/${confirmDel.id}`);
      toast.success("Beat permanently deleted");
      setConfirmDel(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="p-4 md:p-10 max-w-6xl mx-auto animate-in hq-page-wrap">
      <header className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Beats</h1>
        <p className="text-sm text-zinc-500 mt-1">{beats.length} in total</p>
      </header>

      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search beat..."
            data-testid="beats-search" className="bg-black/40 border-white/10 pl-9" />
        </div>
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`beats-filter-${f.k}`}
              className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${filter === f.k ? "bg-violet-600 text-white" : "bg-white/5 text-zinc-400 hover:bg-white/10"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {beatsQ.loading || beatsQ.error ? (
        <LoadState loading={beatsQ.loading} error={beatsQ.error} onRetry={load} testid="beats" />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-lg text-zinc-300 mb-2">No beats yet.</p>
          <p className="text-sm text-zinc-500">Tap + New to create your first.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-white/5 bg-[#0F0F11] divide-y divide-white/5 overflow-hidden">
          {visible.map((b) => {
            const song = songs.find((s) => s.id === b.linked_song_id);
            return (
              <div key={b.id} data-testid={`beat-row-${b.id}`}>
                <div className="flex items-center gap-3 p-4 hover:bg-white/[0.02]">
                  <button onClick={() => setExpanded(expanded === b.id ? null : b.id)} className="w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center"
                    data-testid={`beat-expand-${b.id}`}>
                    <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${expanded === b.id ? "rotate-180" : ""}`} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{b.name}</span>
                      {b.status === "in_use" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">IN USE</span>}
                      {b.status === "available" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">AVAILABLE</span>}
                      {b.bpm && <span className="mono text-[10px] text-zinc-500">{b.bpm} BPM</span>}
                    </div>
                    {song && (
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        Used in: <Link to={`/songs/${song.id}`} className="text-violet-400 hover:text-violet-300">{song.name}</Link>
                      </div>
                    )}
                    <div className="mt-1.5 flex items-center gap-2">
                      <div className="h-1 w-32 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full bg-violet-500" style={{ width: `${b.progress || 0}%` }} />
                      </div>
                      <span className="mono text-[10px] text-zinc-500">{Math.round(b.progress || 0)}%</span>
                    </div>
                  </div>
                  {isOwner && (
                    <button onClick={() => setEditing(b)} data-testid={`beat-edit-${b.id}`}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-500 hover:text-white hover:bg-white/5">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {b.file_id && (
                    <button onClick={() => play({ name: b.name, artwork_id: null }, { id: b.id, file_id: b.file_id, label: "BEAT" })}
                      data-testid={`beat-play-${b.id}`}
                      className="w-9 h-9 rounded-full bg-violet-600 hover:bg-violet-500 flex items-center justify-center">
                      <Play className="w-4 h-4 text-white ml-0.5" />
                    </button>
                  )}
                </div>
                {expanded === b.id && (
                  <div className="p-4 bg-black/20 space-y-3">
                    {isOwner && (
                      <div className="flex gap-2 flex-wrap">
                        <label className="cursor-pointer">
                          <input type="file" accept="audio/*" className="hidden" onChange={(e) => uploadAudio(b, e.target.files?.[0])} />
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-xs" data-testid={`beat-upload-${b.id}`}>
                            <Upload className="w-3.5 h-3.5" />
                            {b.file_id ? "Replace audio" : "Upload audio"}
                          </span>
                        </label>
                        {b.status === "available" && (
                          <Button size="sm" variant="ghost" onClick={() => setLinkOpen(b)} className="bg-white/5 hover:bg-white/10 rounded-full" data-testid={`beat-link-${b.id}`}>
                            <LinkIcon className="w-3.5 h-3.5 mr-1" /> Link to song
                          </Button>
                        )}
                        {b.status === "in_use" && (
                          <Button size="sm" variant="ghost" onClick={() => unlink(b)} className="bg-white/5 hover:bg-white/10 rounded-full" data-testid={`beat-unlink-${b.id}`}>
                            <Unlink className="w-3.5 h-3.5 mr-1" /> Unlink
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={async () => { await api.post(`/beats/${b.id}/archive`, { reason: "Saved for later" }); load(); }}
                          data-testid={`beat-archive-${b.id}`} className="bg-white/5 hover:bg-white/10 rounded-full">
                          <Archive className="w-3.5 h-3.5 mr-1" /> Archive
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDel(b)} className="bg-red-500/10 hover:bg-red-500/20 text-red-300 rounded-full" data-testid={`beat-delete-${b.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                        </Button>
                      </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {Object.entries(BEAT_LABELS).map(([k, l]) => {
                        const state = b.checklist?.[k] || "pending";
                        return (
                          <button key={k} onClick={() => isOwner && toggleTask(b, k)} disabled={!isOwner}
                            data-testid={`beat-task-${b.id}-${k}`}
                            className={`flex items-center gap-2 p-2 rounded-lg text-xs border ${state === "done" ? "bg-violet-500/10 border-violet-500/40 text-white" : "bg-white/[0.02] border-white/5 text-zinc-400"}`}>
                            <span className={`w-4 h-4 rounded border ${state === "done" ? "bg-violet-500 border-violet-500" : "border-white/20"}`} />
                            {l}
                          </button>
                        );
                      })}
                    </div>
                    {b.notes && <p className="text-xs text-zinc-500 italic">{b.notes}</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <ListSentinel hasMore={hasMore} sentinelRef={sentinelRef} />

      <Dialog open={!!linkOpen} onOpenChange={(o) => !o && setLinkOpen(null)}>
        <DialogContent className="bg-[#141416] border-white/10 text-white">
          <DialogHeader><DialogTitle>Link beat to song</DialogTitle></DialogHeader>
          <Select onValueChange={(v) => linkToSong(linkOpen, v)}>
            <SelectTrigger className="bg-black/40 border-white/10" data-testid="beat-link-select"><SelectValue placeholder="Choose song" /></SelectTrigger>
            <SelectContent className="bg-[#141416] border-white/10 text-white">
              {songs.filter((s) => !s.archived).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="bg-[#141416] border-white/10 text-white">
          <DialogHeader><DialogTitle>Delete beat permanently</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-400">This will permanently delete <span className="text-white font-medium">{confirmDel?.name}</span>, its audio and metadata. Linked songs will remain but the beat reference will be removed.</p>
          <p className="text-xs text-amber-400">This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(null)} className="text-zinc-400">Cancel</Button>
            <Button onClick={permanentDelete} className="bg-red-600 hover:bg-red-500 rounded-full" data-testid="beat-delete-confirm">Delete permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditItemDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} kind="beat" item={editing} onSaved={load} />
    </div>
  );
}
